import React, { useEffect, useState } from "react";
import { api, brl } from "../../lib/api";
import { toast } from "sonner";
import { Gauge, TrendingUp, Users, ShieldAlert, Wallet, ArrowUpRight, RefreshCw, Filter, ListChecks, FlaskConical, GitCompare, Clock, Bot } from "lucide-react";
import CreditTabs from "./CreditTabs";
import AIStatusIndicator from "../../components/AIStatusIndicator";
import { publishAiTest } from "../../lib/aiStatus";

const PURPLE = "#5B2CA0";
const DEEP = "#2B1E4A";
const RISK_ORDER = [
  ["muito_baixo", "Muito baixo", "#2E7D46"], ["baixo", "Baixo", "#3F6B45"],
  ["medio", "Moderado", "#B7891B"], ["elevado", "Elevado", "#C2560A"], ["alto", "Alto", "#B4564F"],
];

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-2xl border border-[#EBE5DF] bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8A7788]"><Icon size={14} color={accent || PURPLE} /> {label}</div>
      <div className="mt-1 font-display text-2xl font-bold" style={{ color: accent || DEEP }}>{value}</div>
    </div>
  );
}

export default function CreditDashboard() {
  const [d, setD] = useState(null);
  const [dec, setDec] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [almost, setAlmost] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [maturation, setMaturation] = useState(null);
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const [{ data }, radar, fn, al, fb, mat, hl] = await Promise.all([
        api.get("/credit/dashboard"),
        api.get("/credit/radar").catch(() => ({ data: [] })),
        api.get("/credit/funnel").catch(() => ({ data: null })),
        api.get("/credit/almost-eligible?limit=50").catch(() => ({ data: [] })),
        api.get("/credit/feedback").catch(() => ({ data: null })),
        api.get("/credit/maturation").catch(() => ({ data: null })),
        api.get("/credit/health").catch(() => ({ data: null })),
      ]);
      setD(data);
      const totalRec = (radar.data || []).reduce((s, r) => s + Math.max(0, Number(r.increase || 0)), 0);
      setDec({ total_recommended_increase: totalRec });
      setFunnel(fn.data);
      setAlmost(al.data || []);
      setFeedback(fb.data);
      setMaturation(mat.data);
      setHealth(hl.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível carregar o dashboard.");
    }
  };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setBusy(true);
    try { await api.post("/credit/recompute"); await load(); } catch { /* noop */ } finally { setBusy(false); }
  };

  if (err) return (
    <div className="rounded-xl border-2 border-[#E0D6F0] bg-white p-6 max-w-xl" data-testid="credit-dashboard-error">
      <p className="text-sm text-[#8B5E5E]">{err}</p>
      <button onClick={load} className="brand mt-3" data-testid="credit-dashboard-retry">Tentar novamente</button>
    </div>
  );
  if (!d) return <div className="text-sm text-[#8A7788]" data-testid="credit-dashboard-loading">Carregando...</div>;

  const n = d.clients_analyzed || 0;
  const byRisk = d.by_risk || {};
  const available = Math.max(0, (d.total_limit_granted || 0) - (d.total_limit_used || 0));

  return (
    <div data-testid="credit-dashboard-page">
      <CreditTabs />
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">CRIS CREDIT</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1 flex items-center gap-2"><Gauge size={28} color={PURPLE} /> Dashboard de Crédito</h1>
          <p className="text-sm text-[#5C5A57] mt-2">Visão consolidada do Score Cris e dos limites da carteira.</p>
        </div>
        <button onClick={recompute} disabled={busy} className="brand" data-testid="credit-dashboard-recompute">{busy ? "Recalculando..." : "Recalcular"}</button>
      </div>

      {n === 0 ? (
        <div className="rounded-xl border border-[#EBE5DF] bg-white p-10 text-center text-sm text-[#8A7788]" data-testid="credit-dashboard-empty">
          Nenhum cliente analisado ainda. Clique em "Recalcular" para gerar as análises da carteira.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="credit-kpis">
            <KPI icon={TrendingUp} label="Score médio" value={d.avg_score} testid="kpi-avg-score" />
            <KPI icon={Users} label="Clientes analisados" value={n} testid="kpi-clients" />
            <KPI icon={ArrowUpRight} label="Elegíveis p/ aumento" value={d.eligible_for_increase} accent="#2E7D46" testid="kpi-eligible" />
            <KPI icon={ShieldAlert} label="Em atenção/revisão" value={d.needs_review} accent="#C2560A" testid="kpi-review" />
            <KPI icon={Wallet} label="Limite concedido" value={brl(d.total_limit_granted)} testid="kpi-granted" />
            <KPI icon={Wallet} label="Limite utilizado" value={brl(d.total_limit_used)} testid="kpi-used" />
            <KPI icon={Wallet} label="Limite disponível" value={brl(available)} accent="#2E7D46" testid="kpi-available" />
            <KPI icon={ArrowUpRight} label="Aumento recomendado (total)" value={brl(dec?.total_recommended_increase || 0)} accent={PURPLE} testid="kpi-rec-increase" />
          </div>

          <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-risk-distribution">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Distribuição por Score Cris (risco)</h2>
            <div className="space-y-2">
              {RISK_ORDER.map(([k, label, c]) => {
                const v = byRisk[k] || 0;
                const pct = n ? Math.round((v / n) * 100) : 0;
                return (
                  <div key={k} className="flex items-center gap-3" data-testid={`risk-bar-${k}`}>
                    <span className="w-28 text-[12px] text-[#5C5A57]">{label}</span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-[#F2EFEA]">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c }} />
                    </div>
                    <span className="w-16 text-right text-[12px] font-bold" style={{ color: c }}>{v} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
            {d.score_dropped > 0 && <div className="mt-3 text-[12px] text-[#B4564F]">{d.score_dropped} cliente(s) tiveram queda de score na última análise.</div>}
          </section>

          {d.dry_run && (
            <section className="mt-6 rounded-2xl border border-[#E7DBF7] bg-[#FBF9FF] p-5" data-testid="credit-dryrun">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-full bg-[#EDE3FB] px-2 py-0.5 text-[10px] font-bold text-[#5B2CA0]">Dry-run</span>
                <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Performance do Motor (simulação)</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KPI icon={ArrowUpRight} label="Seriam aprovados" value={d.dry_run.would.WOULD_APPROVE} accent="#2E7D46" testid="dry-would-approve" />
                <KPI icon={ShieldAlert} label="Revisão manual" value={d.dry_run.would.WOULD_REVIEW} accent="#C2560A" testid="dry-would-review" />
                <KPI icon={ShieldAlert} label="Bloqueados" value={d.dry_run.would.BLOCKED} accent="#B4564F" testid="dry-blocked" />
                <KPI icon={TrendingUp} label="Possível redução" value={d.dry_run.would.WOULD_REDUCE} testid="dry-would-reduce" />
                <KPI icon={Wallet} label="Aumento simulado (total)" value={brl(d.dry_run.total_simulated_increase)} accent={PURPLE} testid="dry-total-increase" />
                <KPI icon={Wallet} label="Aumento médio simulado" value={brl(d.dry_run.avg_simulated_increase)} testid="dry-avg-increase" />
                <KPI icon={ArrowUpRight} label="Limite dinâmico elegível" value={d.dry_run.dynamic_eligible} accent={PURPLE} testid="dry-dynamic" />
              </div>
              <p className="mt-3 text-[11px] text-[#8A7788]">Modo DRY-RUN: nenhuma alteração de limite é aplicada automaticamente. Todas as decisões continuam manuais.</p>
            </section>
          )}

          <FunnelSection funnel={funnel} />
          <AlmostEligibleSection rows={almost} />
          <MaturationSection maturation={maturation} />
          <PolicySimulator />
          <FeedbackSection feedback={feedback} />
          <MotorHealthSection health={health} onReload={load} />
        </>
      )}
    </div>
  );
}

function FunnelSection({ funnel }) {
  if (!funnel || !funnel.total) return null;
  const max = funnel.stages[0]?.count || 1;
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-funnel">
      <div className="mb-3 flex items-center gap-2">
        <Filter size={16} color={PURPLE} />
        <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Funil de Elegibilidade</h2>
      </div>
      <div className="space-y-2">
        {funnel.stages.map((s) => {
          const w = Math.round((s.count / max) * 100);
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
              <span className="w-56 text-[12px] text-[#5C5A57]">{s.label}</span>
              <div className="h-4 flex-1 overflow-hidden rounded-full bg-[#F2EFEA]">
                <div className="h-full rounded-full" style={{ width: `${w}%`, background: `linear-gradient(90deg,${PURPLE},${DEEP})` }} />
              </div>
              <span className="w-24 text-right text-[12px] font-bold" style={{ color: DEEP }}>{s.count} ({s.pct}%)</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-[#8A7788] sm:grid-cols-4">
        <span data-testid="funnel-drop-history">Histórico insuficiente: <b>{funnel.drop.insufficient_history}</b></span>
        <span data-testid="funnel-drop-blocked">Com bloqueio: <b>{funnel.drop.blocked}</b></span>
        <span data-testid="funnel-drop-notincrease">Sem aumento: <b>{funnel.drop.not_increase}</b></span>
        <span data-testid="funnel-dynamic">Limite dinâmico: <b>{funnel.dynamic_eligible}</b></span>
      </div>
    </section>
  );
}

function AlmostEligibleSection({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-almost-eligible">
      <div className="mb-3 flex items-center gap-2">
        <ListChecks size={16} color="#2E7D46" />
        <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Quase elegíveis ({rows.length})</h2>
      </div>
      <div className="space-y-2">
        {rows.slice(0, 25).map((r) => (
          <div key={r.client_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EBE5DF] px-3 py-2" data-testid={`almost-row-${r.client_id}`}>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold" style={{ color: DEEP }}>{r.client_name || "Cliente"}</div>
              <div className="text-[11px] text-[#8A7788]">Score {r.global_score} · {r.global_label} · limite {brl(r.current_limit)}</div>
            </div>
            <span className="rounded-full bg-[#FBF6E7] px-2.5 py-1 text-[11px] font-bold text-[#B7891B]" data-testid={`almost-missing-${r.client_id}`}>{r.missing}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const SIM_FIELDS = [
  { key: "auto_approve_min_score", label: "Score mínimo p/ automático", step: 10, def: 700 },
  { key: "min_paid_purchases", label: "Compras quitadas mínimas", step: 1, def: 3 },
  { key: "dynamic_limit_min_score", label: "Score mín. limite dinâmico", step: 10, def: 700 },
  { key: "max_increase_pct_display", label: "Teto de aumento (%)", step: 5, def: 30 },
];

function PolicySimulator() {
  const [form, setForm] = useState(() => SIM_FIELDS.reduce((a, f) => ({ ...a, [f.key]: f.def }), {}));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const policy = {
        auto_approve_min_score: Number(form.auto_approve_min_score),
        min_paid_purchases: Number(form.min_paid_purchases),
        dynamic_limit_min_score: Number(form.dynamic_limit_min_score),
        max_increase_pct: Number(form.max_increase_pct_display) / 100,
      };
      const { data } = await api.post("/credit/policy/simulate?limit=300", { policy });
      setRes(data);
    } catch { setRes(null); toast.error("Não foi possível simular a política. Tente novamente."); }
    finally { setBusy(false); }
  };

  const Row = ({ label, cur, sim, fmt }) => (
    <div className="flex items-center justify-between gap-2 border-b border-[#F2EFEA] py-1.5 text-[12px]">
      <span className="text-[#5C5A57]">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-[#8A7788]">{fmt ? fmt(cur) : cur}</span>
        <GitCompare size={12} className="text-[#B0A6B8]" />
        <span className="font-bold" style={{ color: PURPLE }}>{fmt ? fmt(sim) : sim}</span>
      </span>
    </div>
  );

  return (
    <section className="mt-6 rounded-2xl border border-[#E7DBF7] bg-[#FBF9FF] p-5" data-testid="credit-policy-simulator">
      <div className="mb-3 flex items-center gap-2">
        <FlaskConical size={16} color={PURPLE} />
        <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Simulador de Política</h2>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {SIM_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-[10px] uppercase tracking-widest text-[#8A7788] mb-1">{f.label}</label>
            <input type="number" step={f.step} value={form[f.key]} data-testid={`sim-input-${f.key}`}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full rounded-lg border border-[#E0D6F0] p-2 text-sm" />
          </div>
        ))}
      </div>
      <button onClick={run} disabled={busy} className="brand mt-3" data-testid="sim-run-btn">{busy ? "Simulando..." : "Simular impacto"}</button>
      {res && (
        <div className="mt-4 rounded-xl border border-[#E7DBF7] bg-white p-4" data-testid="sim-result">
          <div className="mb-2 text-[11px] text-[#8A7788]">Comparando <b>{res.analyzed}</b> clientes · atual → simulado</div>
          {res.rules_delta?.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1" data-testid="sim-rules-delta">
              {res.rules_delta.map((r) => (
                <span key={r.key} className="rounded-full bg-[#F2EFEA] px-2 py-0.5 text-[10px] text-[#5C5A57]">{r.label}: {String(r.from)} → <b>{String(r.to)}</b></span>
              ))}
            </div>
          )}
          <Row label="Seriam aprovados" cur={res.current.would.WOULD_APPROVE} sim={res.simulated.would.WOULD_APPROVE} />
          <Row label="Elegíveis ao automático" cur={res.current.eligible_auto} sim={res.simulated.eligible_auto} />
          <Row label="Iriam a revisão" cur={res.current.would.WOULD_REVIEW} sim={res.simulated.would.WOULD_REVIEW} />
          <Row label="Bloqueados" cur={res.current.blocked} sim={res.simulated.blocked} />
          <Row label="Limite dinâmico elegível" cur={res.current.dynamic_eligible} sim={res.simulated.dynamic_eligible} />
          <Row label="Score médio" cur={res.current.avg_score} sim={res.simulated.avg_score} />
          <Row label="Aumento simulado (total)" cur={res.current.total_simulated_increase} sim={res.simulated.total_simulated_increase} fmt={brl} />
          <div className="mt-2 flex items-center justify-between text-[12px]">
            <span className="text-[#5C5A57]">Deixariam de ser bloqueados</span>
            <span className="font-bold" style={{ color: res.unblocked_delta > 0 ? "#2E7D46" : DEEP }} data-testid="sim-unblocked">{res.unblocked_delta}</span>
          </div>
          <p className="mt-2 text-[11px] text-[#8A7788]">{res.note}</p>
        </div>
      )}
    </section>
  );
}

const WOULD_LABEL = { WOULD_APPROVE: "Seria aprovado", WOULD_MAINTAIN: "Manteria", WOULD_REVIEW: "Revisão", WOULD_REDUCE: "Redução", BLOCKED: "Bloqueado" };

function MaturationSection({ maturation }) {
  if (!maturation) return null;
  const b = maturation.buckets || {};
  if (!maturation.has_data) {
    return (
      <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-maturation">
        <div className="mb-2 flex items-center gap-2"><Clock size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Maturação do Motor</h2></div>
        <div className="rounded-xl bg-[#FBFAFE] p-4 text-[12px] text-[#8A7788]" data-testid="maturation-empty">Coorte ainda em maturação.</div>
      </section>
    );
  }
  const cards = [
    { k: "waiting_30", label: "Aguardando 30 dias" }, { k: "evaluable_30", label: "Avaliáveis em 30 dias" },
    { k: "waiting_60", label: "Aguardando 60 dias" }, { k: "evaluable_60", label: "Avaliáveis em 60 dias" },
    { k: "waiting_90", label: "Aguardando 90 dias" }, { k: "concluded_90", label: "Concluídas em 90 dias" },
  ];
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-maturation">
      <div className="mb-3 flex items-center gap-2"><Clock size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Maturação do Motor</h2></div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.k} className="rounded-xl border border-[#EBE5DF] p-3 text-center" data-testid={`maturation-${c.k}`}>
            <div className="text-xl font-bold" style={{ color: DEEP }}>{b[c.k] ?? 0}</div>
            <div className="text-[10px] text-[#8A7788]">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-[#5C5A57]">
        <span data-testid="maturation-monitored">{maturation.total_monitored} decisão(ões) em acompanhamento</span>
        <span data-testid="maturation-partial">{maturation.partial_eval} com avaliação parcial</span>
        {maturation.days_until_first_30d != null && (
          <span data-testid="maturation-first30" className="font-bold" style={{ color: PURPLE }}>Primeira coorte de 30 dias fecha em {maturation.days_until_first_30d} dia(s)</span>
        )}
      </div>
    </section>
  );
}

function MotorHealthSection({ health, onReload }) {
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  if (!health) return null;
  const runTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/credit/ai/test", {}); setTest(data); publishAiTest(data); toast.success(data.ok ? `OpenAI OK (${data.latency_ms}ms)` : "Caiu no fallback determinístico"); }
    catch { toast.error("Falha ao testar IA"); }
    finally { setTesting(false); }
  };
  const Item = ({ label, value, testid }) => (
    <div className="flex items-center justify-between border-b border-[#F2EFEA] py-1.5 text-[12px]" data-testid={testid}>
      <span className="text-[#5C5A57]">{label}</span><span className="font-bold" style={{ color: DEEP }}>{value}</span>
    </div>
  );
  const last = health.last_call || {};
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-motor-health">
      <div className="mb-3 flex items-center gap-2"><ShieldAlert size={16} color={PURPLE} /><h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Saúde do Motor</h2></div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <Item label="Engine version" value={health.engine_version} testid="health-engine" />
          <Item label="Policy version" value={health.policy_version} testid="health-policy" />
          <Item label="Modo de automação" value={<span className="rounded-full bg-[#EDE3FB] px-2 py-0.5 text-[10px] text-[#5B2CA0]">{health.automation_mode}</span>} testid="health-mode" />
          <Item label="Redução automática" value={health.automatic_limit_reduction ? "Ativa" : "Desativada"} testid="health-reduction" />
          <Item label="Fila de recálculo" value={health.pending_recalc_queue} testid="health-queue" />
          <Item label="Coortes em maturação" value={health.cohorts_in_maturation} testid="health-cohorts" />
          <Item label="Próx. jobs" value={<span className="text-[11px]">{health.next_jobs?.automation} · resumo {health.next_jobs?.weekly_summary}</span>} testid="health-jobs" />
        </div>
        <div className="rounded-xl border border-[#E7DBF7] bg-[#FBF9FF] p-3" data-testid="health-ai-card">
          <div className="mb-2 flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-lg text-white" style={{ background: `linear-gradient(135deg,${PURPLE},${DEEP})` }}><Bot size={13} /></span>
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: DEEP }}>IA Explicativa: OpenAI</span>
          </div>
          <Item label="Modelo" value={health.ai_model} testid="health-ai-model" />
          <Item label="Status" value={health.ai_enabled ? "Habilitada" : "Desabilitada"} testid="health-ai-status" />
          <Item label="Última chamada" value={last.created_at ? new Date(last.created_at).toLocaleString("pt-BR") : "—"} testid="health-ai-last" />
          <Item label="Latência (última)" value={last.latency_ms != null ? `${last.latency_ms}ms` : "—"} testid="health-ai-latency" />
          <Item label="Fallback (24h / 7d)" value={`${health.ai_fallback_24h} / ${health.ai_fallback_7d}`} testid="health-ai-fallback" />
          <button onClick={runTest} disabled={testing} className="brand mt-3 w-full" data-testid="health-ai-test-btn">{testing ? "Testando..." : "Testar IA (OpenAI)"}</button>
          <AIStatusIndicator compact />
          {test && (
            <div className="mt-2 rounded-lg bg-white px-2.5 py-1.5 text-[11px]" data-testid="health-ai-test-result">
              {test.ok ? <span className="text-[#2E7D46]">✓ OpenAI respondeu em {test.latency_ms}ms (JSON válido)</span>
                       : <span className="text-[#B4564F]">Fallback determinístico usado ({test.error || "erro"}) — o motor segue funcionando</span>}
            </div>
          )}
          <p className="mt-2 text-[10px] text-[#B0A6B8]">A IA apenas explica os números. Nunca calcula score/limite nem decide aprovação.</p>
        </div>
      </div>
    </section>
  );
}

function FeedbackSection({ feedback }) {
  const [win, setWin] = useState("30");
  if (!feedback || !feedback.windows) return null;
  const f = feedback.windows[win];
  if (!f) return null;
  return (
    <section className="mt-6 rounded-2xl border border-[#EBE5DF] bg-white p-5" data-testid="credit-feedback">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} color={PURPLE} />
          <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: DEEP }}>Feedback do Motor</h2>
        </div>
        <div className="flex gap-1" data-testid="feedback-window-tabs">
          {["30", "60", "90"].map((w) => (
            <button key={w} onClick={() => setWin(w)} data-testid={`feedback-win-${w}`}
              className={`rounded-lg px-3 py-1 text-[12px] font-bold ${win === w ? "text-white" : "text-[#5C5A57] border border-[#D6CFC7]"}`}
              style={win === w ? { background: PURPLE } : {}}>{w}d</button>
          ))}
        </div>
      </div>
      {f.cohort_size === 0 ? (
        <div className="rounded-xl bg-[#FBFAFE] p-4 text-[12px] text-[#8A7788]" data-testid="feedback-empty">
          Ainda não há coorte com {win} dias de histórico. O feedback aparecerá conforme as análises amadurecem (DRY-RUN iniciado recentemente).
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="rounded-xl border border-[#E7DBF7] bg-[#FBF9FF] p-3" data-testid="feedback-shadow">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#5B2CA0]">Recomendação simulada</div>
            {Object.entries(f.shadow_recommendation).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">{WOULD_LABEL[k] || k}</span><b>{v}</b></div>
            ))}
          </div>
          <div className="rounded-xl border border-[#DCEFE0] bg-[#F5FBF6] p-3" data-testid="feedback-manual">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#2E7D46]">Decisão humana</div>
            <div className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">Com decisão</span><b>{f.manual_total}</b></div>
            {Object.entries(f.manual_decision).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">{k}</span><b>{v}</b></div>
            ))}
          </div>
          <div className="rounded-xl border border-[#F1DBC9] bg-[#FDF6EF] p-3" data-testid="feedback-actual">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#C2560A]">Alteração real de limite</div>
            <div className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">Aumentaram</span><b>{f.actual_limit_change.increased}</b></div>
            <div className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">Reduziram</span><b>{f.actual_limit_change.reduced}</b></div>
            <div className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">Sem alteração</span><b>{f.actual_limit_change.unchanged}</b></div>
            <div className="flex justify-between text-[12px]"><span className="text-[#5C5A57]">Delta total</span><b>{brl(f.actual_limit_change.total_delta)}</b></div>
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-[#8A7788]">Coorte de {f.cohort_size} cliente(s). As três colunas são independentes: uma recomendação simulada nunca significa crédito concedido.</p>
    </section>
  );
}
